import { FormEvent, useCallback, useMemo } from "react";
import {
  useUserDetailed,
  useUserUpdate,
  useProfile,
  useMfaReset,
  useClientUpdate,
  useMeClient,
  useHouseholdClients,
  useProfileUpdate,
} from "@common/queries";
import { ClientRoutes } from "@common/constants";
import InputAccordion from "@common/components/InputAccordion";
import { withPageAuthRequired } from "@common/middleware";
import { NextPage } from "next";
import Link from "next/link";
import FamilyMembers from "@modules/settings/components/FamilyMembers";
import { useHouseholdInvitations } from "@modules/settings/queries";
import { useSendHouseholdInvitation } from "@modules/settings/mutations";
import { FormValue } from "@modules/settings/types";
import { useLogout, useCareerStage, useIsAdvisor } from "@common/hooks";
import LoadingIndicator from "@common/components/LoadingIndicator";
import { HiOutlineExternalLink } from "react-icons/hi";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Typography, SelectBox } from "@earned/core";
import Input from "@common/components/Input";
import AddressStateSelect from "@common/components/AddressStateSelect";

type Inputs = {
  addressStreet: string;
  addressStreetLineTwo: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
};

const AddressSchema = yup
  .object({
    addressStreet: yup.string().required("Address is required."),
    addressStreetLineTwo: yup.string(),
    addressCity: yup.string().required("City is required."),
    addressState: yup.string().required("State is required."),
    addressZip: yup.string().required("Zip code is required."),
  })
  .required();

type EmailAlertOptions = {
  key: string;
  name: string;
  value: boolean;
};

const Settings = () => {
  const {
    isLoading: isLoadingDetailedUser,
    data: detailedUser,
    refetch: refetchDetailedUser,
  } = useUserDetailed();

  const {
    data: client,
    refetch: refetchClient,
    isLoading: isLoadingClient,
  } = useMeClient();
  const {
    data: profile,
    refetch: refetchProfile,
    isLoading: isLoadingProfile,
  } = useProfile();
  const { data: clients } = useHouseholdClients();
  const primaryClient = useMemo(() => {
    if (clients) {
      return clients?.find((client) => client.isPrimary);
    }
  }, [clients]);
  const coClients = useMemo(() => {
    if (clients) {
      const coClientsList = clients?.find((client) => !client.isPrimary);
      return coClientsList ? ([coClientsList] as Forme.Client[]) : [];
    }
  }, [clients]);
  const {
    isLoading: isLoadingHouseholdInvitations,
    data: householdInvitations,
    refetch: refetchHouseholdInvitations,
  } = useHouseholdInvitations();

  const familyMembers = useMemo(() => {
    return {
      count: (householdInvitations?.length ?? 0) + (coClients?.length ?? 0),
      householdInvitations,
      coClients: coClients ?? [],
    };
  }, [coClients, householdInvitations]);
  const userPhoneNumber =
    (detailedUser?.mfa_enrollments[0] ?? {}).phone_number ?? "";

  const emailsAlertsOptions: EmailAlertOptions[] = useMemo(() => {
    return [
      {
        key: "taskNotificationsEnabled",
        name: "Tasks & Actions",
        value: client?.taskNotificationsEnabled as boolean,
      },
      {
        key: "documentVaultNotificationsEnabled",
        name: "Document Vault",
        value: client?.documentVaultNotificationsEnabled as boolean,
      },
    ];
  }, [client]);

  const {
    value: careerStageValue,
    options: careerStageOptions,
    isLoading: isLoadingCareerStage,
    refetch: refetchCareerStage,
    mutateAsync: updateCareerStage,
  } = useCareerStage();

  const isAlertsEnabled = useMemo(() => {
    const option = emailsAlertsOptions.find((option) => option.value);
    return !!option;
  }, [emailsAlertsOptions]);

  const { mutateAsync: updateClient } = useClientUpdate();
  const { mutateAsync: updateProfile } = useProfileUpdate();
  const { mutateAsync: updateUser } = useUserUpdate();
  const { mutateAsync: resetMfa } = useMfaReset();

  const {
    mutateAsync: sendHouseholdInvite,
    isLoading: isLoadingSendHouseholdInvitation,
  } = useSendHouseholdInvitation();

  const onResetMfa = useCallback(async () => {
    return new Promise<void>(async (resolve) => {
      const result = await resetMfa();
      window.open(
        result.resetMfa.url,
        "Reset Earned MFA",
        "directories=0,titlebar=0,toolbar=0,left=400,top=400,location=,status=0,menubar=0,scrollbars=no,resizable=no,width=800,height=800"
      );
      resolve();
    });
  }, [resetMfa]);

  const onUserEditSave = useCallback(
    (value: string | boolean, key: string) => {
      return new Promise<void>(async (resolve, reject) => {
        try {
          await updateUser({ [key]: value });
          await refetchDetailedUser();
          resolve();
        } catch (err) {
          reject(err);
        }
      });
    },
    [updateUser, refetchDetailedUser]
  );

  const onClientEditSave = useCallback(
    (value: string | boolean, key: string) => {
      return new Promise<void>(async (resolve, reject) => {
        try {
          await updateClient({ [key]: value });
          await refetchClient();
          resolve();
        } catch (err) {
          reject(err);
        }
      });
    },
    [updateClient, refetchClient]
  );

  const onProfileEditSave = useCallback(
    (value: string | boolean, key: string) => {
      return new Promise<void>(async (resolve, reject) => {
        try {
          await updateProfile({ [key]: value });
          await refetchProfile();
          resolve();
        } catch (err) {
          reject(err);
        }
      });
    },
    [updateProfile, refetchProfile]
  );

  const { setShowLogoutDialog } = useLogout();
  const onClickLogout = useCallback(() => {
    setShowLogoutDialog(true);
  }, [setShowLogoutDialog]);

  const sendInvites = async (values: FormValue[]) => {
    try {
      const invitations = values.map((item) => item.value);
      await sendHouseholdInvite(invitations);
      await refetchHouseholdInvitations();
    } catch (err) {
      console.error(err);
    }
  };
  const { isAdvisorPortal } = useIsAdvisor();

  const isLoading = useMemo(
    () => isLoadingClient || isLoadingProfile || isLoadingDetailedUser,
    [isLoadingClient, isLoadingDetailedUser, isLoadingProfile]
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = useForm<Inputs>({
    resolver: yupResolver(AddressSchema),
    mode: "all",
  });

  return (
    <div className="z-10 flex flex-1 flex-col font-medium">
      <LoadingIndicator isVisible={isLoading} />
      {!isLoading && (
        <div
          className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden p-10"
          style={{ scrollbarGutter: "stable" }}
        >
          <div className="mb-10 flex-none">
            <Typography variant="title-xs" color="primary" weight="medium">
              Profile
            </Typography>
          </div>
          <div className="flex flex-1 flex-col">
            <div className="-mx-10 flex flex-1 flex-col">
              <InputAccordion
                disabled={true}
                label="Legal First Name"
                value={client?.firstName ?? ""}
                onSubmit={(value) =>
                  onClientEditSave(value as string, "firstName")
                }
              />
              <InputAccordion
                disabled={true}
                label="Legal Last Name"
                value={client?.lastName ?? ""}
                onSubmit={(value) =>
                  onClientEditSave(value as string, "lastName")
                }
              />
              <InputAccordion
                label="Address"
                disabled={true}
                value={client?.addressStreet ?? ""}
                returnEvent
                onSubmit={async (event) => {
                  (event as FormEvent).preventDefault();
                  await handleSubmit(async (data) => {
                    await updateClient(data);
                    await refetchClient();
                  })(event as FormEvent);
                }}
              >
                <div className="grid grid-cols-7 gap-1">
                  <div className="col-span-5">
                    <Input
                      autoFocus={true}
                      required={true}
                      label="Address"
                      placeholder="First Line"
                      defaultValue={client?.addressStreet || ""}
                      error={errors.addressStreet}
                      readOnly={isSubmitting}
                      {...register("addressStreet")}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      required={true}
                      label="Unit Number"
                      placeholder="Apt, Suite Etc"
                      defaultValue={client?.addressStreetLineTwo || ""}
                      error={errors.addressStreetLineTwo}
                      readOnly={isSubmitting}
                      {...register("addressStreetLineTwo")}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      required={true}
                      placeholder="City"
                      label="City"
                      defaultValue={client?.addressCity || ""}
                      error={errors.addressCity}
                      readOnly={isSubmitting}
                      {...register("addressCity")}
                    />
                  </div>
                  <div className="col-span-2">
                    <Controller
                      name="addressState"
                      rules={{ required: true }}
                      control={control}
                      defaultValue={client?.addressState || ""}
                      render={({ field }) => (
                        <AddressStateSelect
                          {...field}
                          label="State"
                          error={errors.addressState}
                          onChange={(value: any) =>
                            field.onChange(value.target.value)
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      required={true}
                      label="Zip Code"
                      placeholder="Zip Code"
                      defaultValue={client?.addressZip || ""}
                      error={errors.addressZip}
                      readOnly={isSubmitting}
                      {...register("addressZip")}
                    />
                  </div>
                </div>
              </InputAccordion>
              <InputAccordion
                label="Email"
                value={detailedUser?.email ?? ""}
                onSubmit={(value) => onUserEditSave(value as string, "email")}
                disabled={true}
              />
              <InputAccordion
                label="Password"
                value=""
                displayValue="********"
                onSubmit={(value) =>
                  onUserEditSave(value as string, "password")
                }
                Input={<Input type="password" />}
              />
              <InputAccordion
                label="Phone"
                value={
                  !isLoadingDetailedUser && !userPhoneNumber
                    ? "Reset MFA Required"
                    : userPhoneNumber
                }
                submitText="Reset MFA"
                onSubmit={onResetMfa}
                Input={<Input disabled={true} />}
              />
              {careerStageValue && (
                <InputAccordion
                  label="Career Stage"
                  value={careerStageValue}
                  displayValue={
                    !isLoadingCareerStage ? careerStageValue.name : ""
                  }
                  onSubmit={(value) => {
                    return new Promise<void>(async (resolve, reject) => {
                      try {
                        await updateCareerStage({
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          careerStage: (value as any)
                            .id as Forme.Profile["careerStage"],
                        });
                        await refetchCareerStage();
                        resolve();
                      } catch (err) {
                        reject(err);
                      }
                    });
                  }}
                  Input={
                    <SelectBox
                      selected={careerStageValue}
                      options={careerStageOptions}
                      onChange={() => {
                        /* overwritten by <InputAccordion/>*/
                      }}
                    />
                  }
                />
              )}
              <InputAccordion
                label="NPI number"
                value={profile?.npiNumber ?? ""}
                onSubmit={(value) =>
                  onProfileEditSave(value as string, "npiNumber")
                }
              />
              {profile?.specialty && (
                <InputAccordion
                  label="Specialty"
                  value={profile?.specialty ?? ""}
                  onSubmit={(value) =>
                    onProfileEditSave(value as string, "specialty")
                  }
                  disabled={true}
                />
              )}
              {!isAdvisorPortal && (
                <>
                  {client?.isPrimary ? (
                    <InputAccordion
                      label="Family Members"
                      value={`${familyMembers.count} users`}
                      showButtons={false}
                    >
                      <FamilyMembers
                        data={familyMembers.householdInvitations || []}
                        coClients={familyMembers.coClients}
                        onSubmit={sendInvites}
                        isLoading={
                          isLoadingSendHouseholdInvitation ||
                          isLoadingHouseholdInvitations
                        }
                      />
                    </InputAccordion>
                  ) : (
                    <InputAccordion
                      label="Primary Account"
                      disabled
                      value={`${primaryClient?.firstName ?? ""} ${
                        primaryClient?.lastName ?? ""
                      }`}
                      showButtons={false}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex-none py-10">
            <Typography variant="title-xs" color="primary" weight="medium">
              Privacy
            </Typography>
          </div>
          <div className="flex flex-1 flex-col">
            <div className="-mx-10 flex flex-1 flex-col">
              <InputAccordion
                label="Email Alerts"
                displayValue={isAlertsEnabled ? "On" : "Off"}
                onSubmit={(value, key) =>
                  onClientEditSave(value as boolean, key as string)
                }
                switchOptions={emailsAlertsOptions}
                showButtons={false}
                switchInput
              />
              <InputAccordion
                label="Terms & Conditions"
                disabled
                displayValue={
                  <Link href={ClientRoutes.Disclosures}>
                    <HiOutlineExternalLink className="cursor-pointer" />
                  </Link>
                }
                showButtons={false}
              />
              <InputAccordion
                label="Privacy Policy"
                disabled
                displayValue={
                  <a
                    href="https://www.earnedwealth.com/legal/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <HiOutlineExternalLink />
                  </a>
                }
                showButtons={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withPageAuthRequired(Settings) as NextPage;
